import React, { useEffect, useState, useRef, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { InfoIcon } from 'lucide-react';
interface InfoTooltipProps {
  content: string;
  /** Optional accessible label override; defaults to "More information" */
  label?: string;
  /** Where to position the tooltip relative to the icon. Defaults to 'top'. */
  placement?: 'top' | 'bottom';
  /** Horizontal alignment of the tooltip relative to the trigger. */
  align?: 'center' | 'left' | 'right';
}
export function InfoTooltip({
  content,
  label = 'More information',
  placement = 'top',
  align = 'center'
}: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const tipId = useId();
  const TOOLTIP_WIDTH = 256; // matches w-64
  const GAP = 8;
  useLayoutEffect(() => {
    if (!isVisible || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let left: number;
    if (align === 'left') {
      left = rect.left;
    } else if (align === 'right') {
      left = rect.right - TOOLTIP_WIDTH;
    } else {
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    }
    // Clamp to viewport with 8px margin
    const maxLeft = window.innerWidth - TOOLTIP_WIDTH - 8;
    left = Math.max(8, Math.min(left, maxLeft));
    const top = placement === 'bottom' ? rect.bottom + GAP : rect.top - GAP;
    setCoords({
      top,
      left
    });
  }, [isVisible, placement, align]);
  // Dismiss on Escape (WCAG 2.2 — Content on Hover or Focus)
  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isVisible]);
  return <>
      <button ref={triggerRef} type="button" aria-label={label} aria-describedby={isVisible ? tipId : undefined} className="ml-1 p-1.5 -m-1 text-gray-500 hover:text-gray-800 rounded inline-flex items-center align-middle focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-1" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onFocus={() => setIsVisible(true)} onBlur={() => setIsVisible(false)} data-id="element-15256" data-mp="3lhj5ieb4as3i">
        <InfoIcon size={14} aria-hidden="true" data-id="element-15257" data-mp="3caexd9xbf3w" />
      </button>
      {isVisible && coords && typeof document !== 'undefined' && createPortal(<div id={tipId} role="tooltip" style={{
      position: 'fixed',
      top: coords.top,
      left: coords.left,
      width: TOOLTIP_WIDTH,
      transform: placement === 'bottom' ? undefined : 'translateY(-100%)'
    }} className="p-2.5 bg-gray-900 text-white text-xs leading-snug font-normal normal-case tracking-normal rounded-md shadow-xl z-[1000] pointer-events-none" data-id="element-15258" data-mp="3prlnqhgrelz2">
            {content}
          </div>, document.body)}
    </>;
}